import 'typeahead.js/dist/typeahead.bundle';
import { SuggestionType } from 'types/suggestion-type';
import { ResultChild, ResultGroup, SearchFieldResponse } from 'types/search-field-response';
import { FetchHandler } from 'components/fetchhandler';
import { isMobile } from 'helpers/is-mobile';
import { toggleButtonState } from 'components/button-helper';
import Localization from './localization';
import WebPushNotificationSubscribeHandler from './web-push-notification-subscribe-handler';

export class HeaderSearch {

    private searchFieldInput !: JQuery;
    private campaignList !: JQuery;
    private container !: JQuery;
    private searchSubmitButton !: JQuery;

    public constructor() {
        this.init();
    }

    private init() : void {
        this.setOpacityOnCampaignList();
        this.initSearchFieldTypeahead();
        this.getContainer();

        WebPushNotificationSubscribeHandler().init();
    }

    private getContainer() : JQuery {
        if (this.container === null || typeof this.container === 'undefined') {
            this.container = isMobile() ? $('.mobile-header-search') : $('.fixed-header');
        }

        return this.container;
    }

    /**
     * Sets the opacity to .5 on the list page when the user searches in the search bar
     */
    private setOpacityOnCampaignList() : void {
        this.getContainer().find('.js-header-search form').on('submit.submitHeaderSearch', () => {
            this.getCampaignList().addClass('filter-loading');
            toggleButtonState(this.getSearchSubmitButton(), false);
        });
    }

    private getSearchSubmitButton() : JQuery {
        if (typeof this.searchSubmitButton === 'undefined') {
            this.searchSubmitButton = this.getContainer().find('.js-header-search button[type="submit"]');
        }

        return this.searchSubmitButton;
    }

    private getCampaignList() : JQuery {
        if (this.campaignList === null || typeof this.campaignList === 'undefined') {
            this.campaignList = $('#campaign-list');
        }

        return this.campaignList;
    }

    private getSearchFieldInput() : JQuery {
        if (typeof this.searchFieldInput === 'undefined') {
            this.searchFieldInput = $('#headerSearch');
        }

        return this.searchFieldInput;
    }

    private initSearchFieldTypeahead() : void {
        const typeaheadOptions = {
            minLength: 3,
            highlight: true,
        };

        const typeaheadDataset = {
            display: 'name',
            limit: 50,
            templates: {
                suggestion: (data : SuggestionType) => this.getSuggestionTemplate(data),
            },
            source: this.searchFieldSource.bind(this),
        };

        (this.getSearchFieldInput() as any).typeahead(typeaheadOptions, typeaheadDataset);
        this.handleTypeaheadEvents();
    }

    private getSuggestionTemplate(data : SuggestionType) : string {
        if (typeof data.error !== 'undefined') {
            return `<div class="tt-error">${data.name}</div>`;
        }
        if (typeof data.url === 'undefined') {
            return `<div class="tt-not-selectable">${data.name}</div>`;
        }
        return `<div class="tt-selectable">${data.name}</div>`;
    }

    private searchFieldSource(
        query : string,
        process : (results : SuggestionType[]) => void,
        asyncProcess : (results : SuggestionType[]) => void,
    ) : void {
        const url = new URL(this.getSearchFieldInput().data('autocompleteUrl'), window.location.origin);
        url.searchParams.append('term', query);

        FetchHandler.create()
        .setMethod('GET')
        .setXMLHttpRequest()
        .setUrl(url.toString())
        .send()
        .then((response : SearchFieldResponse) => {
            const suggestions = this.buildSuggestionResults(response);
            asyncProcess(suggestions);
        })
        .catch((error) => {
            throw error;
        });
    }

    private buildSuggestionResults(response : SearchFieldResponse) : SuggestionType[] {
        const resultElements: SuggestionType[] = [];
        if (typeof response.results !== 'undefined') {
            if (response.results.length > 0) {
                response.results.forEach((result : ResultGroup) => {
                    resultElements.push({ name: result.text });
                    if (result.children.length > 0) {
                        result.children.forEach((child : ResultChild) => {
                            resultElements.push({
                                id: child.id,
                                name: child.text,
                                url: child.url,
                            });
                        });
                    }
                });
            }
            else {
                resultElements.push({
                    name: new Localization().getMessage('searchbar_error_no_elements'),
                    error: 'noElements',
                });
            }
        }
        return resultElements;
    }

    private handleTypeaheadEvents() : void {
        this.getSearchFieldInput()
        .on('typeahead:select', (event : JQuery.TriggeredEvent, suggestion : SuggestionType) => {
            this.handleTypeaheadSelect(suggestion);
        })
        .on('typeahead:beforeselect', (event : JQuery.TriggeredEvent, suggestion : SuggestionType) => {
            this.handleBeforeSelect(event, suggestion);
        })
        .on('typeahead:cursorchange', (event : JQuery.TriggeredEvent, suggestion : SuggestionType) => {
            this.handleCursorChange(event, suggestion);
        })
        .on('typeahead:beforeautocomplete', (event : JQuery.TriggeredEvent, suggestion : SuggestionType) => {
            this.handleBeforeAutocomplete(event, suggestion);
        });
    }

    private handleTypeaheadSelect(suggestion : SuggestionType) : void {
        if (typeof suggestion.url !== 'undefined') {
            toggleButtonState(this.getSearchSubmitButton(), false);
            window.location.href = suggestion.url;
        }
    }

    private handleBeforeSelect(event : JQuery.TriggeredEvent, suggestion : SuggestionType) : void {
        if (typeof suggestion.url === 'undefined') {
            event.preventDefault();
        }
    }

    private handleCursorChange(event : JQuery.TriggeredEvent, suggestion : SuggestionType) : void {
        if (typeof suggestion === 'undefined' || typeof suggestion.url === 'undefined') {
            const keyDownEvent = jQuery.Event('keydown');
            keyDownEvent.which = 40;
            $(event.target).trigger(keyDownEvent);
        }
    }

    private handleBeforeAutocomplete(
        event : JQuery.TriggeredEvent,
        suggestion : SuggestionType,
    ) : void {
        if (typeof suggestion.url === 'undefined') {
            event.preventDefault();
        }
    }

}
